import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import AuthBrandPanel from "components/Auth/AuthBrandPanel";
import AuthField from "components/Auth/AuthField";
import arrowLeft from "assets/images/auth/arrow-left.svg";
import eyeIcon from "assets/images/auth/eye-icon.svg";
import passwordSuccessCheck from "assets/images/auth/password-success-check-a.svg";
import { userResetPassword } from "utils/authApi";
import "styles/auth.css";

function PasswordControl({
  name,
  label,
  value,
  error,
  visible,
  onChange,
  onToggle,
}) {
  return (
    <AuthField id={name} label={label} required error={error}>
      <div className="auth-password">
        <input
          id={name}
          className={`auth-control${error ? " auth-control--error" : ""}`}
          type={visible ? "text" : "password"}
          value={value}
          onChange={onChange}
          placeholder="Enter password"
          maxLength={20}
          autoComplete="new-password"
        />
        <button
          className="auth-password__toggle"
          type="button"
          onClick={onToggle}
          aria-label={
            visible
              ? `Hide ${label.toLowerCase()}`
              : `Show ${label.toLowerCase()}`
          }
        >
          <img src={eyeIcon} alt="" />
        </button>
      </div>
      {name === "password" && !error && (
        <span className="auth-field__hint">Must be at least 8 characters</span>
      )}
    </AuthField>
  );
}

function SetNewPassword() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [values, setValues] = useState({ password: "", confirmPassword: "" });
  const [errors, setErrors] = useState({
    password: "",
    confirmPassword: "",
    token: token ? "" : "This password reset link is invalid or missing.",
  });
  const [visible, setVisible] = useState({
    password: false,
    confirmPassword: false,
  });
  const [resetComplete, setResetComplete] = useState(false);

  const updateValue = (name) => (event) => {
    setValues((current) => ({ ...current, [name]: event.target.value }));
    setErrors((current) => ({ ...current, [name]: "" }));
  };

  const submitPassword = (event) => {
    event.preventDefault();
    const nextErrors = {
      password: "",
      confirmPassword: "",
      token: errors.token,
    };
    if (values.password.length < 8)
      nextErrors.password = "Password must be at least 8 characters";
    else if (values.password.length > 20)
      nextErrors.password = "Password must not exceed 20 characters";
    if (!values.confirmPassword)
      nextErrors.confirmPassword = "Please confirm your password";
    else if (values.password !== values.confirmPassword)
      nextErrors.confirmPassword = "Passwords do not match";
    setErrors(nextErrors);
    if (nextErrors.password || nextErrors.confirmPassword || nextErrors.token)
      return;

    userResetPassword(
      {
        token,
        password: values.password,
        confirmPassword: values.confirmPassword,
      },
      (data) => {
        if (!data.Result_Status) {
          setErrors((current) => ({ ...current, token: data.Result_Message }));
          return;
        }
        setResetComplete(true);
      },
      dispatch
    );
  };

  return (
    <main
      className={`auth-page ${
        resetComplete
          ? "auth-page--password-success"
          : "auth-page--new-password"
      }`}
    >
      <AuthBrandPanel />
      <section
        className={`auth-panel ${
          resetComplete
            ? "auth-panel--password-success"
            : "auth-panel--new-password"
        }`}
      >
        {resetComplete ? (
          <div className="auth-password-success">
            <div className="auth-password-success__icon">
              <img src={passwordSuccessCheck} alt="" />
            </div>
            <div className="auth-password-success__copy">
              <h2>Password reset successful</h2>
              <p>
                Your password has been successfully updated. You can now sign in
                with your new password.
              </p>
            </div>
            <button
              className="auth-button auth-button--primary"
              type="button"
              onClick={() => navigate("/login", { replace: true })}
            >
              Sign in
            </button>
          </div>
        ) : (
          <form
            className="auth-form auth-form--new-password"
            onSubmit={submitPassword}
            noValidate
          >
            <header className="auth-title">
              <h2>Set new password</h2>
              <p>Create a new password for your account</p>
            </header>
            <div className="auth-inputs">
              <PasswordControl
                name="password"
                label="Password"
                value={values.password}
                error={errors.password}
                visible={visible.password}
                onChange={updateValue("password")}
                onToggle={() =>
                  setVisible((current) => ({
                    ...current,
                    password: !current.password,
                  }))
                }
              />
              <PasswordControl
                name="confirmPassword"
                label="Confirm password"
                value={values.confirmPassword}
                error={errors.confirmPassword}
                visible={visible.confirmPassword}
                onChange={updateValue("confirmPassword")}
                onToggle={() =>
                  setVisible((current) => ({
                    ...current,
                    confirmPassword: !current.confirmPassword,
                  }))
                }
              />
              {errors.token && (
                <span className="auth-field__error" role="alert">
                  {errors.token}
                </span>
              )}
            </div>
            <div className="auth-actions auth-actions--new-password">
              <button
                className="auth-button auth-button--primary"
                type="submit"
                disabled={!token}
              >
                Reset password
              </button>
              <Link className="auth-back-link" to="/login">
                <img src={arrowLeft} alt="" />
                Back to sign in
              </Link>
            </div>
          </form>
        )}
        <footer className="auth-legal">
          <a href="#privacy">Privacy</a>
          <span>·</span>
          <a href="#terms">Terms</a>
        </footer>
      </section>
    </main>
  );
}

export default SetNewPassword;
